import { hash } from 'immutable';
import { combineActions, handleActions } from 'redux-actions';

import { accountsActions } from '../accounts';
import { resourceStoreTypes, resourceUndoUpserters, resourceUpserters } from '../core';
import { coreActions } from '../core-actions';
import { transactionsTransformers, type transactionsTypes } from '../transactions';

import * as actions from './scheduledTransactionsActions';
import * as scheduledTransactionsTypes from './scheduledTransactionsTypes';

const QcsSyncScheduledTransactionsStoreFactory =
  resourceStoreTypes.createTypedQcsSyncResourceStoreFactory<scheduledTransactionsTypes.ScheduledTransactionType>();

const scheduledTransactionsReducer = handleActions<
  resourceStoreTypes.QcsSyncResourceStore<scheduledTransactionsTypes.ScheduledTransactionType>,
  | coreActions.SanitizeAction
  | ReturnType<typeof actions.createScheduledTransaction>
  | ReturnType<typeof actions.createScheduledTransactionSuccess>
  | ReturnType<typeof actions.updateScheduledTransaction>
  | ReturnType<typeof actions.updateScheduledTransactionSuccess>
  | ReturnType<typeof actions.deleteScheduledTransaction>
  | ReturnType<typeof actions.deleteScheduledTransactionSuccess>
  | ReturnType<typeof actions.batchScheduledTransactions>
  | ReturnType<typeof actions.batchScheduledTransactionsSuccess>
  | ReturnType<typeof actions.getScheduledTransactions>
  | ReturnType<typeof actions.getScheduledTransactionsSuccess>
  | ReturnType<typeof actions.getScheduledTransactionsFailure>
  | ReturnType<typeof actions.createBillScoutScheduleSuccess>
>(
  {
    [coreActions.SANITIZE]: (state) =>
      state.set(
        'resourcesById',
        state.resourcesById.map((resource) =>
          resource.mergeDeep({
            transaction: {
              payee: hash(resource.transaction?.payee || '').toString(),
              memo: hash(resource.transaction?.memo || '').toString(),
            } as transactionsTypes.TransactionProps, // NOTE: `mergeDeep` doesn't recursively type nested records as Partial<> like it should, so we typecast here.
          }),
        ),
      ),

    [actions.createScheduledTransaction.type]: (state, { payload: scheduledTransaction }) =>
      resourceUpserters.upsertResource(state, scheduledTransaction),
    [actions.createScheduledTransactionSuccess.type]: (state, action) =>
      resourceUndoUpserters.upsertResourceSetupUndo(state, action, actions.deleteScheduledTransaction),
    [actions.updateScheduledTransaction.type]: (state, action) =>
      resourceUndoUpserters.upsertResourceSetupUndo(state, action, actions.updateScheduledTransaction),
    [actions.updateScheduledTransactionSuccess.type]: (state, { payload: scheduledTransaction }) =>
      resourceUpserters.upsertResource(state, scheduledTransaction),
    [actions.deleteScheduledTransaction.type]: (state, action) =>
      resourceUndoUpserters.upsertResourceSetupUndo(state, action, actions.createScheduledTransaction),
    [actions.deleteScheduledTransactionSuccess.type]: (state, { payload: scheduledTransaction }) =>
      resourceUpserters.upsertResource(state, scheduledTransaction),
    [actions.batchScheduledTransactions.type]: (state, action) =>
      resourceUndoUpserters.upsertResourcesSetupUndo(state, action, actions.batchScheduledTransactions, false),
    [actions.batchScheduledTransactionsSuccess.type]: (state, action) =>
      resourceUndoUpserters.upsertResourcesSetupUndo(state, action, actions.batchScheduledTransactions, true),

    [combineActions(
      actions.createScheduledTransactionFailure,
      actions.updateScheduledTransactionFailure,
      actions.deleteScheduledTransactionFailure,
      actions.batchScheduledTransactionsFailure,
    ).toString()]: (
      state: resourceStoreTypes.QcsSyncResourceStore<scheduledTransactionsTypes.ScheduledTransactionType>,
      { payload: error, asyncDispatch }: any,
    ) => {
      asyncDispatch(actions.getScheduledTransactions());
      return resourceUpserters.resetResources(state, error);
    },

    [actions.getScheduledTransactions.type]: (state) => state.merge({ isLoading: true }),
    [actions.getScheduledTransactionsSuccess.type]: (state, { payload: resources }) =>
      resourceUpserters.upsertResources(state, resources),
    [actions.getScheduledTransactionsFailure.type]: (state, { payload: error }) =>
      resourceUpserters.completeWithError(state, error),
    [actions.createBillScoutScheduleSuccess.type]: (state, { payload, meta, asyncDispatch }) => {
      let newState = state;
      if (meta?.response?.data?.schedule) {
        const consumableTxn = transactionsTransformers.transformQcsTransactionToTransaction(
          meta.response.data.schedule.transaction,
        );
        const shouldUpdateToSubscription = meta.props?.isSubscription && consumableTxn?.isSubscription === false; // override default isBill value

        const newScheduledTxn = scheduledTransactionsTypes.mkScheduledTransaction({
          // update schedTxns
          ...meta.response.data.schedule,
          type: shouldUpdateToSubscription
            ? scheduledTransactionsTypes.STTypeEnum.SUBSCRIPTION
            : meta.response.data.schedule.type,
          clientId: payload.clientId,
          recurrence: scheduledTransactionsTypes.mkRecurrence({ ...meta.response.data.schedule.recurrence }),
          transaction: shouldUpdateToSubscription
            ? consumableTxn.merge({ isBill: false, isSubscription: true })
            : consumableTxn,
        });
        const createScheduledTransactionAction = {
          ...meta,
          payload: newScheduledTxn,
        };
        // hacky way of updating BillScout to use the correct flag when creating a subscription instead of a bill
        if (shouldUpdateToSubscription) {
          asyncDispatch(
            actions.updateScheduledTransaction(
              scheduledTransactionsTypes.mkScheduledTransaction({
                id: newScheduledTxn.id,
                transaction: newScheduledTxn.transaction,
              }),
            ),
          );
        }
        newState = resourceUndoUpserters.upsertResourceSetupUndo(
          state,
          createScheduledTransactionAction,
          actions.deleteScheduledTransaction,
        );
      }

      return newState;
    },

    // accounts cascade deletion
    [accountsActions.applyAccountsChanges.toString()]: (
      state: any,
      { payload, asyncDispatch }: { payload: any[]; asyncDispatch: any },
    ) => {
      const deletedAccountIds = payload.filter((account) => account.isDeleted).map((account) => account.id);
      if (deletedAccountIds.length) {
        asyncDispatch(actions.getScheduledTransactions(undefined, { context: 'applyAccountsChanges' })); /// TODO: remove after bump is enabled
        const resourcesById = state.resourcesById.filter(
          (scheduledTransaction: scheduledTransactionsTypes.ScheduledTransactionType) =>
            !deletedAccountIds.includes(scheduledTransaction.transaction?.accountId),
        );
        return state.merge({ resourcesById });
      }
      return state;
    },
  },
  QcsSyncScheduledTransactionsStoreFactory(),
);

export default scheduledTransactionsReducer;
